import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { ChartView } from './ChartWindow';

// ADMIN APPROVAL FLASH (owner 2026-10-19: "every trade recommended by AI should show on desktop as a flash with the
// chart; I approve or reject; only admin"). Polls /api/admin/approvals every 3 s; a new recommendation flashes on
// screen (beep + desktop notification when allowed) with the live chart, the AI's reason and a countdown.
// one shared audio context, unlocked on the first tap/click (phones block sound until the user touches the page)
let AC = null;
function audio() {
  try { if (!AC) { const C = window.AudioContext || window.webkitAudioContext; AC = new C(); } if (AC.state === 'suspended') AC.resume(); } catch (e) { AC = null; }
  return AC;
}
if (typeof window !== 'undefined') {
  const unlock = () => { audio(); window.removeEventListener('pointerdown', unlock); window.removeEventListener('keydown', unlock); };
  window.addEventListener('pointerdown', unlock); window.addEventListener('keydown', unlock);
}
function beep() {
  const ctx = audio(); if (!ctx) return;
  try {
    [0, 0.3, 0.6].forEach(t => { const o = ctx.createOscillator(); const g = ctx.createGain(); o.type = 'square';
      o.frequency.value = t === 0.3 ? 1320 : 990; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.35, ctx.currentTime + t); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.2); });
  } catch (e) { /* no audio */ }
  try { if (navigator.vibrate) navigator.vibrate([300, 150, 300]); } catch (e) { /* ignore */ }
}
// notification through the service worker (works on Android Chrome too); falls back to a page notification
async function notify(a) {
  try {
    if (!window.Notification || Notification.permission !== 'granted') return;
    const title = `⚡ AI TRADE: BUY ${a.trade_symbol}`;
    const opts = { body: `${a.lane} · premium ₹${a.premium} · ${a.left >= 0 ? `approve within ${a.left}s` : 'no time limit'}`, requireInteraction: true, tag: a.id,
                   vibrate: [300, 150, 300], renotify: true };
    const reg = navigator.serviceWorker ? await navigator.serviceWorker.getRegistration() : null;
    if (reg && reg.showNotification) { await reg.showNotification(title, opts); return; }
    const n = new Notification(title, opts);
    n.onclick = () => { try { window.focus(); n.close(); } catch (e) { /* ignore */ } };
  } catch (e) { /* ignore */ }
}

// CARD RE-CHECK (owner 2026-10-30: 'if I only watch a card and time passes or the AI no longer wants the trade, tell me'):
// every 60 s the server re-checks the waiting pick (age, option move, the AI's read NOW, NO CHASE, Renko). Green = still
// fine; red lines = what changed, with one alarm + notification the moment it turns.
function CardCheck({ id, sym }) {
  const [c, setC] = useState(null);
  const was = useRef(null);
  useEffect(() => {
    let alive = true;
    const f = async () => {
      try {
        const r = await api.checkApproval(id);
        if (!alive || !r || r.status !== 'success') return;
        if (was.current === true && !r.ok) {
          beep();
          try {
            if (window.Notification && Notification.permission === 'granted')
              new Notification(`⚠ ${sym}: things changed`, { body: (r.warnings || []).join(' · '), tag: `chk-${id}` });   // eslint-disable-line no-new
          } catch (e) { /* ignore */ }
        }
        was.current = r.ok; setC(r);
      } catch (e) { /* keep last */ }
    };
    f(); const iv = setInterval(f, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, [id, sym]);
  if (!c) return <div style={{ color: '#888', fontSize: '0.75rem' }}>checking the AI's view now…</div>;
  // chance of profit from the remembered patterns of your past decisions (My Decisions page)
  const odds = c.odds ? (
    <div style={{ fontSize: '0.74rem', color: '#9aa4b7' }}>
      🧠 similar picks: <b style={{ color: c.odds.win_pct >= 50 ? '#00ff88' : '#ff6b6b' }}>{c.odds.win_pct}% won</b>, avg {c.odds.avg_pct > 0 ? '+' : ''}{c.odds.avg_pct}%
      {(c.odds.bad || []).length > 0 && <span style={{ color: '#ffb020' }}> · careful: {c.odds.bad.join(', ')}</span>}
    </div>) : null;
  if (c.ok) return (<>
    <div style={{ color: '#00ff88', fontSize: '0.78rem', fontWeight: 700 }}>
      ✔ still valid now{c.ai && c.ai.signal ? ` - AI ${c.ai.signal} score ${c.ai.score}` : ''}{c.chg_pct != null ? ` · option ${c.chg_pct >= 0 ? '+' : ''}${c.chg_pct}% since the pick` : ''}
    </div>{odds}</>);
  return (
    <div style={{ background: '#3a0f0f', border: '1px solid #ff4444', borderRadius: 4, padding: '3px 6px', margin: '2px 0' }}>
      <div style={{ color: '#ff6b6b', fontSize: '0.8rem', fontWeight: 800 }}>⚠ CHANGED since the pick - Approve will ask you first:</div>
      {(c.warnings || []).map((w, i) => <div key={i} style={{ color: '#ffb3b3', fontSize: '0.76rem' }}>• {w}</div>)}
      {odds}
    </div>);
}

// live price of the recommended option, every 2 s, with the change since the pick
function LivePrem({ sym, exchange, base }) {
  const [q, setQ] = useState(null);
  useEffect(() => {
    let alive = true;
    const f = async () => { try { const r = await api.getQuote(sym, exchange || 'NSE'); if (alive && r && r.status === 'success') setQ(r); } catch (e) { /* keep last */ } };
    f(); const iv = setInterval(f, 2000);
    return () => { alive = false; clearInterval(iv); };
  }, [sym, exchange]);
  const ltp = q && +q.ltp;
  if (!ltp) return <span style={{ color: '#888', fontSize: '0.8rem' }}>· now …</span>;
  const ch = base ? ((ltp - base) / base) * 100 : 0;
  return (
    <span style={{ fontSize: '0.85rem' }}>· now <b style={{ color: '#fff' }}>₹{ltp.toFixed(2)}</b>{' '}
      <span style={{ color: ch >= 0 ? '#00ff88' : '#ff6b6b' }}>({ch >= 0 ? '+' : ''}{ch.toFixed(1)}%)</span>
      {q.bid ? <span style={{ color: '#888', fontSize: '0.72rem' }}> bid {(+q.bid).toFixed(2)} / ask {(+q.ask).toFixed(2)}</span> : null}
    </span>
  );
}

// ENABLE PUSH ALERTS on this device (Safari needs a tap; iPhone needs the Home-Screen app)
function b64ToU8(b64) {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}
export function PushButton() {
  const [state, setState] = useState('checking');   // checking | on | off | unsupported | ios-home
  // the hint can be hidden (owner 2026-10-22: on a phone the floating boxes covered the page); comes back after 1 day
  const [hidden, setHidden] = useState(() => { try { return Date.now() - (+localStorage.getItem('push_hint_hidden') || 0) < 86400000; } catch (e) { return false; } });
  const hide = () => { setHidden(true); try { localStorage.setItem('push_hint_hidden', String(Date.now())); } catch (e) { /* ignore */ } };
  useEffect(() => {
    (async () => {
      const ios = /iPhone|iPad|iPod/.test(navigator.userAgent);
      const standalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) { setState(ios && !standalone ? 'ios-home' : 'unsupported'); return; }
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        const sub = await reg.pushManager.getSubscription();
        setState(sub && Notification.permission === 'granted' ? 'on' : 'off');
      } catch (e) { setState('off'); }
    })();
  }, []);
  const enable = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setState('off'); alert('Notifications are blocked - allow them for this site in the browser settings.'); return; }
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const k = await api.getPushKey();
      let sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(k.key) });
      const r = await api.pushSubscribe(sub.toJSON());
      setState(r && r.status === 'success' ? 'on' : 'off');
    } catch (e) { alert('Could not switch alerts on: ' + (e && e.message ? e.message : e)); }
  };
  if (state === 'on' || state === 'checking' || hidden) return null;
  const narrow = window.innerWidth < 640;
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const iosVer = (navigator.userAgent.match(/OS (\d+)_(\d+)/) || []).slice(1).join('.');
  const standalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
  return (
    <div style={{ position: 'fixed', left: 12, bottom: narrow ? 124 : 12, right: narrow ? 12 : 'auto', zIndex: 9000, background: '#1a1a2e', border: '1px solid #ffd700', borderRadius: 8,
      padding: '0.45rem 1.6rem 0.45rem 0.7rem', fontSize: narrow ? '0.74rem' : '0.8rem', maxWidth: narrow ? 'none' : 300 }}>
      <button onClick={hide} title="Hide for today" style={{ position: 'absolute', top: 2, right: 4, background: 'transparent', border: 'none', color: '#aaa', fontSize: '0.9rem', cursor: 'pointer' }}>✕</button>
      {state === 'ios-home' || (state === 'unsupported' && ios && !standalone)
        ? <span>🔔 iPhone alerts: this is a <b>Safari tab</b> - alerts can't work here. Delete the old icon, then in Safari tap <b>Share</b> → <b>Add to Home Screen</b> with <b>"Open as Web App" ON</b>, and open the app from that icon (iOS {iosVer || '?'}).</span>
        : state === 'unsupported'
        ? <span>🔕 This browser can't receive trade alerts{ios ? ` (iOS ${iosVer || '?'} - needs 16.4+, opened from the Home Screen icon)` : ''}.</span>
        : <><span>🔔 Get a phone alert when a trade is taken or exits: </span>
          <button className="btn btn-start" style={{ padding: '0.15rem 0.6rem', marginLeft: 4 }} onClick={enable}>Enable trade alerts</button></>}
    </div>
  );
}

export default function ApprovalFlash() {
  // AUTO TRADE ON / OFF (owner 2026-10-22, replaces the 'AI picks ON/OFF' alerts switch): ON = every trade is placed
  // automatically (no pop-ups); OFF = every option trade pops up here for Approve / Reject and the AI tracks how each
  // choice turned out (Dashboard: 'What your decisions teach'). Pop-ups, sound and phone alerts are always on.
  const [autoOn, setAutoOn] = useState(null);
  const onRef = useRef(true);
  const [list, setList] = useState([]);
  const listRef = useRef([]);
  listRef.current = list;
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    let alive = true;
    const q = () => api.getAutoTrade().then(r => { if (alive && r && r.status === 'success') setAutoOn(!!r.on); }).catch(() => {});
    q(); const iv = setInterval(q, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  const toggleAuto = async () => {
    const v = !autoOn;
    try {
      const r = await api.setAutoTrade(v);
      if (r && r.status === 'success') { setAutoOn(!!r.on); setMsg({ t: 'success', m: r.message }); setTimeout(() => setMsg(null), 8000); }
      else setMsg({ t: 'error', m: (r && r.message) || 'Could not switch' });
    } catch (e) { setMsg({ t: 'error', m: 'Server not reachable' }); }
  };
  const seen = useRef(new Set());
  const decided = useRef(new Set());   // decided on this screen - never shown again
  useEffect(() => {
    try { if (window.Notification && Notification.permission === 'default') Notification.requestPermission(); } catch (e) { /* ignore */ }
    try { if (navigator.serviceWorker) navigator.serviceWorker.register('/sw.js'); } catch (e) { /* ignore */ }
    let alive = true;
    const q = async () => {
      try {
        const [r, ra] = await Promise.all([api.getApprovals(), api.getAdvice().catch(() => null)]);
        if (!alive || !r || r.status !== 'success') return;
        const p = [...(r.pending || []), ...((ra && ra.status === 'success' && ra.pending) || [])];
        p.forEach(a => {
          if (!seen.current.has(a.id)) {
            seen.current.add(a.id);
            if (onRef.current) { beep(); notify(a); }
          }
        });
        setList(p.filter(x => !decided.current.has(x.id)));
      } catch (e) { /* keep last */ }
    };
    q(); const iv = setInterval(q, 3000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  // repeat the alarm every 10 s until answered
  useEffect(() => {
    if (!list.length) return undefined;
    const iv = setInterval(() => { if (onRef.current && listRef.current.some(x => Date.now() / 1000 - x.ts < 120)) beep(); }, 10000);   // beep for the first 2 min of a new card
    return () => clearInterval(iv);
  }, [list.length]);
  // local countdown between polls
  const [, tick] = useState(0);
  useEffect(() => { const iv = setInterval(() => tick(x => x + 1), 1000); return () => clearInterval(iv); }, []);
  const a = list[0];
  // flash the browser-tab title while a recommendation waits (seen from any other tab / program)
  const orig = useRef(document.title);
  useEffect(() => {
    if (!list.length) { document.title = orig.current; return undefined; }
    let on = false;
    const iv = setInterval(() => { on = !on; document.title = on ? `⚡ AI TRADE - approve (${list.length})` : orig.current; }, 900);
    return () => { clearInterval(iv); document.title = orig.current; };
  }, [list.length]);
  const [ask, setAsk] = useState(null);   // {cur, warnings}: 'still trade? Yes / No' (owner 2026-10-30)
  const decide = async (cur, action, force = false) => {
    // gone from the screen at once; the order result arrives as a message (placing for several users takes seconds)
    if (!cur) return;
    decided.current.add(cur.id);
    setList(l => l.filter(x => x.id !== cur.id));
    setMsg({ t: 'success', m: action === 'approve' ? `Placing ${cur.trade_symbol} for all users…` : action === 'exit' ? `Exiting ${cur.trade_symbol}…`
      : action === 'keep' ? `Keeping ${cur.trade_symbol}` : `Rejected ${cur.trade_symbol}` });
    try {
      const r = cur.kind === 'advice' ? await api.decideAdvice(cur.id, action) : await api.decideApproval(cur.id, action, force);
      if (r && r.status === 'confirm') {
        // something changed since the pick: nothing placed - ask the owner (the card comes back until answered)
        decided.current.delete(cur.id);
        setList(l => (l.some(x => x.id === cur.id) ? l : [cur, ...l]));
        setMsg(null);
        setAsk({ cur, warnings: r.warnings || [] });
        beep();
        return;
      }
      const m = { t: r.status === 'success' && !(r.failed || []).length ? 'success' : 'error', m: r.message || action };
      setMsg(m);
      setTimeout(() => setMsg(cur2 => (cur2 === m ? null : cur2)), 25000);
    } catch (e) { setMsg({ t: 'error', m: 'Server not reachable' }); }
  };
  // Esc = reject
  useEffect(() => {
    if (!a) return undefined;
    const k = (e) => { if (e.key === 'Escape') decide(a, a && a.kind === 'advice' ? 'keep' : 'reject'); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  });   // eslint-disable-line react-hooks/exhaustive-deps
  const shown = [...list].sort((p, q) => (p.ts || 0) - (q.ts || 0) || String(p.id).localeCompare(String(q.id))).slice(0, 8);   // up to 8, oldest first - a fixed order (owner 30 Oct: cards moved under the finger)
  const two = shown.length > 1 && window.innerWidth > 900;
  return (
    <>
      <PushButton />
      {autoOn !== null && (
        <button onClick={toggleAuto} title={autoOn ? 'Auto trade ON: every trade is placed automatically. Tap to choose trades yourself.'
          : 'Auto trade OFF: every trade waits for your Approve / Reject. Tap to switch back to automatic.'}
          style={{ position: 'fixed', right: 12, bottom: 84, zIndex: 9001, borderRadius: 20, padding: '0.35rem 0.85rem', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
            border: `1px solid ${autoOn ? '#00ff88' : '#ffb020'}`, background: autoOn ? '#0f3d24' : '#3a2a0a', color: autoOn ? '#00ff88' : '#ffcc66' }}>
          {autoOn ? '🤖 Auto trade: ON' : `✋ Auto trade: OFF — you choose${list.length ? ` (${list.length} waiting)` : ''}`}
        </button>)}
      {msg && <div onClick={() => setMsg(null)} style={{ position: 'fixed', left: 12, right: 12, bottom: 16, zIndex: 10001, background: msg.t === 'success' ? '#0f3d24' : '#3a0d0d',
        border: `1px solid ${msg.t === 'success' ? '#00c46a' : '#ff4444'}`, color: '#fff', padding: '0.7rem 2rem 0.7rem 0.9rem', borderRadius: 8, fontSize: '0.9rem',
        boxShadow: '0 4px 16px rgba(0,0,0,0.6)', cursor: 'pointer' }}>{msg.m}<span style={{ position: 'absolute', right: 10, top: 8, color: '#aaa' }}>✕</span></div>}
      {ask && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 10003, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
          <div className="card" style={{ maxWidth: 520, width: '100%', margin: 0, border: '2px solid #ffb020' }}>
            <div style={{ color: '#ffb020', fontWeight: 800, fontSize: '1rem', marginBottom: 6 }}>⚠ {ask.cur.trade_symbol}: things changed since the AI picked it</div>
            {ask.warnings.map((w, i) => <div key={i} style={{ color: '#ffcccc', fontSize: '0.85rem', margin: '3px 0' }}>• {w}</div>)}
            <div style={{ color: '#fff', fontWeight: 700, margin: '10px 0 8px' }}>Do you still want to trade it?</div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-start" style={{ padding: '0.45rem 1.2rem' }} onClick={() => { const c = ask.cur; setAsk(null); decide(c, 'approve', true); }}>Yes, place it</button>
              <button className="btn btn-stop" style={{ padding: '0.45rem 1.2rem' }} onClick={() => { const c = ask.cur; setAsk(null); decide(c, 'reject'); }}>No, reject</button>
              <button className="btn" style={{ padding: '0.45rem 1rem', marginLeft: 'auto' }} onClick={() => setAsk(null)}>Keep watching</button>
            </div>
          </div>
        </div>)}
      {shown.length > 0 && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 10000, overflowY: 'auto', padding: '12px' }}>
          <style>{'@keyframes apflash { 0%,100% { box-shadow: 0 0 0 rgba(255,215,0,0) } 50% { box-shadow: 0 0 28px rgba(255,215,0,0.9) } }'}</style>
          <div style={{ color: '#ffd700', fontWeight: 700, marginBottom: 8 }}>
            ⚡ {list.length} AI pick{list.length > 1 ? 's' : ''} waiting{list.length > 8 ? ` — showing 8, ${list.length - 8} more after these` : ''}
            <span style={{ color: '#888', fontWeight: 400, fontSize: '0.75rem' }}> · Approve = placed for all options users (paper/live, own account) · ✕ / Esc = reject · no time limit (closes at 15:15)</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: two ? '1fr 1fr' : '1fr', gap: 10 }}>
            {shown.map(x => {
              const age = Math.max(0, Math.round(Date.now() / 1000 - x.ts));
              const lf = x.left != null && x.left >= 0 ? x.left : -1;
              if (x.kind === 'advice') return (
                <div key={x.id} className="card" style={{ margin: 0, border: '2px solid #ff9f1a', animation: 'apflash 1s ease-in-out 3' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                    <b style={{ color: '#ff9f1a' }}>⚠ RENKO TURNED — {x.trade_symbol}</b>
                    <span style={{ color: '#ccc', fontSize: '0.8rem' }}>entry ₹{x.entry} · now ₹{x.now}</span>
                    <b style={{ color: x.pct >= 0 ? '#00ff88' : '#ff6b6b' }}>{x.pct >= 0 ? '+' : ''}{x.pct}%</b>
                    <button className="btn" title="Close = keep" style={{ marginLeft: 'auto', padding: '0.05rem 0.5rem' }} onClick={() => decide(x, 'keep')}>✕</button>
                  </div>
                  <div style={{ color: '#ffb020', fontSize: '0.75rem' }}>{x.text} · held by: {(x.users || []).join(', ')}</div>
                  <ChartView symbol={x.symbol} exchange={x.exchange} height={two ? 320 : 400} />
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
                    <button className="btn btn-stop" style={{ padding: '0.4rem 1.1rem' }} onClick={() => decide(x, 'exit')}>Exit now (all users)</button>
                    <button className="btn btn-start" style={{ padding: '0.4rem 1.1rem' }} onClick={() => decide(x, 'keep')}>Keep</button>
                  </div>
                </div>
              );
              return (
                <div key={x.id} className="card" style={{ margin: 0, border: `2px solid ${x.side === 'CE' ? '#00ff88' : '#ff4444'}`, animation: 'apflash 1s ease-in-out 3' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                    <b style={{ color: x.side === 'CE' ? '#00ff88' : '#ff6b6b' }}>BUY {x.trade_symbol}</b>
                    <span style={{ color: '#ccc', fontSize: '0.8rem' }}>pick ₹{x.premium} · lot {x.lot_size}{x.score ? ` · score ${x.score}` : ''}</span>
                    <LivePrem sym={x.trade_symbol} exchange={x.exchange} base={x.premium} />
                    <span title="No time limit - the card waits until you answer (closes at the 15:15 square-off)" style={{ marginLeft: 'auto', color: age >= 300 ? '#ff6b6b' : '#ffb020', fontWeight: 700 }}>{lf >= 0 ? `${lf}s` : age < 60 ? `${age}s ago` : `waiting ${Math.floor(age / 60)} min${age >= 300 ? ' - check the price now' : ''}`}</span>
                    <button className="btn" title="Close = reject" style={{ padding: '0.05rem 0.5rem' }} onClick={() => decide(x, 'reject')}>✕</button>
                  </div>
                  {x.cost_1lot > 0 && (
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, margin: '2px 0', color: x.live_free != null && x.cost_1lot > x.live_free * 0.95 ? '#ff6b6b' : '#8ee6a8' }}>
                      1 lot = ₹{Number(x.cost_1lot).toLocaleString('en-IN')}
                      {x.live_free != null && (x.cost_1lot > x.live_free * 0.95
                        ? ` · ⚠ your LIVE free cash is ₹${Number(x.live_free).toLocaleString('en-IN')} - too expensive, will NOT be placed live`
                        : ` · your LIVE free cash ₹${Number(x.live_free).toLocaleString('en-IN')} - fits`)}
                    </div>)}
                  {x.warn && String(x.warn).split(' | ').map((w, i) => (
                    <div key={i} style={{ color: '#ff6b6b', fontSize: '0.8rem', fontWeight: 700, margin: '2px 0', background: '#3a0f0f', borderRadius: 4, padding: '2px 6px' }}>⚠ {w}</div>))}
                  {/* the live re-check changes every minute: fixed height (scrolls inside) so the buttons below never move */}
                  <div style={{ height: '4.6em', overflowY: 'auto' }}><CardCheck id={x.id} sym={x.trade_symbol} /></div>
                  <div style={{ color: '#ffd700', fontSize: '0.72rem' }}>{x.lane}</div>
                  <div style={{ color: '#aaa', fontSize: '0.75rem', marginBottom: '0.3rem' }}>{(x.reasons || []).join(' · ')}</div>
                  {/* owner 30 Oct: Approve / Reject just above the chart */}
                  <div style={{ display: 'flex', gap: '0.5rem', margin: '0.3rem 0' }}>
                    <button className="btn btn-start" style={{ padding: '0.45rem 1.4rem', fontWeight: 800 }} onClick={() => decide(x, 'approve')}>✓ Approve</button>
                    <button className="btn btn-stop" style={{ padding: '0.45rem 1.4rem', fontWeight: 800 }} onClick={() => decide(x, 'reject')}>✕ Reject</button>
                  </div>
                  <ChartView symbol={x.symbol} exchange={x.exchange} height={two ? 320 : 400} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
